/**
 * The example gallery.
 *
 * Sources are real `.flow` files under `examples/` at the repository root
 * rather than string literals in this file: they stay diffable, they open in an
 * editor with the same highlighting a user's own document gets, and the e2e
 * suite can feed the parser exactly the bytes the UI ships. Vite inlines them
 * at build time through the `?raw` suffix, so nothing is fetched at runtime.
 *
 * Titles and descriptions are carried in both locales here rather than in the
 * i18n bundles, because the gallery is content: adding an example should be one
 * `.flow` file plus one entry, with no second file to keep in sync.
 */
import accountSignup from '../../examples/account-signup.flow?raw';
import benchmarkLarge from '../../examples/benchmark-large.flow?raw';
import errorHandling from '../../examples/error-handling.flow?raw';
import gettingStarted from '../../examples/getting-started.flow?raw';
import nestedDecisions from '../../examples/nested-decisions.flow?raw';
import orderItemsLoop from '../../examples/order-items-loop.flow?raw';
import passwordRecovery from '../../examples/password-recovery.flow?raw';
import paymentProcessing from '../../examples/payment-processing.flow?raw';
import reusableFunctions from '../../examples/reusable-functions.flow?raw';
import roleSwitch from '../../examples/role-switch.flow?raw';
import userLogin from '../../examples/user-login.flow?raw';

/**
 * Category values are the `examples.category.*` message-key suffixes verbatim,
 * so the gallery can label a group with `t(\`examples.category.${category}\`)`
 * and no lookup table. `MessageKey` is derived from the English catalogue, so a
 * category with no matching key is a compile error at the call site rather than
 * a blank heading — which is why these are camelCase while ids are kebab-case.
 */
export type ExampleCategory = 'basics' | 'controlFlow' | 'functions' | 'patterns' | 'algorithms';

export interface ExampleDefinition {
  /** Kebab-case slug; matches the `.flow` filename and is used in the URL hash. */
  readonly id: string;
  readonly titleEn: string;
  readonly titlePt: string;
  readonly descriptionEn: string;
  readonly descriptionPt: string;
  readonly category: ExampleCategory;
  /** Full pseudocode source, ready to drop into the editor. */
  readonly source: string;
}

/** Loaded on first run and by the "reset to the example" action. */
export const PRIMARY_EXAMPLE_ID = 'getting-started';

/**
 * Gallery order. Deliberately not alphabetical: the list is meant to be read
 * top to bottom, starting from the smallest program that shows every core idea.
 */
export const EXAMPLES: readonly ExampleDefinition[] = [
  {
    id: 'getting-started',
    titleEn: 'Getting started',
    titlePt: 'Primeiros passos',
    descriptionEn:
      'The whole language in fifteen lines: a start terminator, one decision with an else branch, and a function reached by a call.',
    descriptionPt:
      'A linguagem inteira em quinze linhas: um terminador de início, uma decisão com ramo else e uma função alcançada por uma chamada.',
    category: 'basics',
    source: gettingStarted,
  },
  {
    id: 'user-login',
    titleEn: 'User login',
    titlePt: 'Login de usuário',
    descriptionEn:
      'Sign-in wrapped in a retry loop that locks the account after three failed attempts.',
    descriptionPt:
      'Autenticação dentro de um laço de repetição que bloqueia a conta após três tentativas malsucedidas.',
    category: 'patterns',
    source: userLogin,
  },
  {
    id: 'account-signup',
    titleEn: 'Account signup',
    titlePt: 'Cadastro de conta',
    descriptionEn:
      'Field validation in its own function, a duplicate-email check, and a confirmation mail — annotated with icons and colours.',
    descriptionPt:
      'Validação de campos em uma função própria, verificação de e-mail duplicado e mensagem de confirmação, anotados com ícones e cores.',
    category: 'functions',
    source: accountSignup,
  },
  {
    id: 'password-recovery',
    titleEn: 'Password recovery',
    titlePt: 'Recuperação de senha',
    descriptionEn:
      'A single-use token, an expiry window and the reset itself, with a separate exit for every way the link can fail.',
    descriptionPt:
      'Um token de uso único, uma janela de expiração e a redefinição em si, com uma saída separada para cada forma de falha do link.',
    category: 'patterns',
    source: passwordRecovery,
  },
  {
    id: 'payment-processing',
    titleEn: 'Payment processing',
    titlePt: 'Processamento de pagamento',
    descriptionEn:
      'Renamed branch labels, a bounded repeat/until retry against the gateway, and a switch over the payment method.',
    descriptionPt:
      'Rótulos de ramo personalizados, uma repetição repeat/until limitada contra o gateway e um switch sobre o método de pagamento.',
    category: 'patterns',
    source: paymentProcessing,
  },
  {
    id: 'order-items-loop',
    titleEn: 'Order item loop',
    titlePt: 'Laço sobre itens do pedido',
    descriptionEn:
      'A for-each over order lines that skips what is out of stock and breaks out of the pass on a hard failure.',
    descriptionPt:
      'Um for each sobre as linhas do pedido que pula itens sem estoque e interrompe o laço diante de uma falha grave.',
    category: 'controlFlow',
    source: orderItemsLoop,
  },
  {
    id: 'role-switch',
    titleEn: 'Role routing',
    titlePt: 'Roteamento por papel',
    descriptionEn:
      'A switch with four roles and a default that treats anything unrecognised as an access denial.',
    descriptionPt:
      'Um switch com quatro papéis e um default que trata qualquer valor desconhecido como acesso negado.',
    category: 'controlFlow',
    source: roleSwitch,
  },
  {
    id: 'nested-decisions',
    titleEn: 'Nested decisions',
    titlePt: 'Decisões aninhadas',
    descriptionEn:
      'A deliberately dense triage tree: four levels of nesting with a loop at the bottom of the deepest branch.',
    descriptionPt:
      'Uma árvore de triagem propositalmente densa: quatro níveis de aninhamento com um laço no fundo do ramo mais profundo.',
    category: 'controlFlow',
    source: nestedDecisions,
  },
  {
    id: 'reusable-functions',
    titleEn: 'Reusable functions',
    titlePt: 'Funções reutilizáveis',
    descriptionEn:
      'Three functions, one calling another, and a call written above the declaration it points at — order never matters.',
    descriptionPt:
      'Três funções, uma chamando a outra, e uma chamada escrita acima da declaração à qual se refere: a ordem nunca importa.',
    category: 'functions',
    source: reusableFunctions,
  },
  {
    id: 'error-handling',
    titleEn: 'Error handling',
    titlePt: 'Tratamento de erros',
    descriptionEn:
      'An import job where every failure mode gets its own terminator and the classification happens in a function that returns a verdict.',
    descriptionPt:
      'Uma importação em que cada modo de falha tem seu próprio terminador e a classificação acontece em uma função que retorna um veredito.',
    category: 'patterns',
    source: errorHandling,
  },
];

/**
 * The performance fixture. Deliberately outside `EXAMPLES`: it is far too long
 * to be useful in the gallery, and it exists only so benchmarks and the "very
 * large document" tests have a stable, realistic input.
 */
export const BENCHMARK_SOURCE: string = benchmarkLarge;

const BY_ID: ReadonlyMap<string, ExampleDefinition> = new Map(
  EXAMPLES.map((example) => [example.id, example]),
);

export function getExample(id: string): ExampleDefinition | undefined {
  return BY_ID.get(id);
}

/** Display order of the category headings in the gallery. */
const CATEGORY_ORDER: readonly ExampleCategory[] = [
  'basics',
  'controlFlow',
  'functions',
  'patterns',
  'algorithms',
];

const GROUPED: ReadonlyArray<{
  category: ExampleCategory;
  items: readonly ExampleDefinition[];
}> = CATEGORY_ORDER.map((category) => ({
  category,
  items: EXAMPLES.filter((example) => example.category === category),
})).filter((group) => group.items.length > 0);

/**
 * Examples grouped for the gallery, empty categories dropped.
 *
 * The result is computed once and handed back by reference, so callers can use
 * it directly as a memo or effect dependency without re-rendering on every pass.
 */
export function examplesByCategory(): ReadonlyArray<{
  category: ExampleCategory;
  items: readonly ExampleDefinition[];
}> {
  return GROUPED;
}
